import throttle from 'lodash/throttle';
import { onReady } from './util/onReady';

interface Section extends HTMLElement {
    id: string;
}

interface SectionLink extends HTMLElement {
    dataset: {
        id: string;
    };
}

let animating = false;

function setActiveSection(sectionLink: SectionLink | null): void {
    if (!sectionLink) return;
    document.querySelector('.section-link.active')?.classList.remove('active');
    sectionLink.classList.add('active');
}

function findSection(id: string, sections: Section[]): Section | undefined {
    return sections.find((sec) => sec.id === id);
}

function scrollToSection(section: Section): void {
    if (animating) return;
    animating = true;

    const top = section.offsetTop - 45;
    window.scrollTo({
        top,
        behavior: 'smooth',
    });

    setTimeout(() => {
        animating = false;
        document.querySelector('.js-child-nav')?.classList.remove('expanded');
        document.querySelector('.js-local-nav')?.classList.remove('expanded');

        const scrollPosBeforeHashChange = window.scrollY;
        window.location.hash = section.id;
        window.scrollTo(0, scrollPosBeforeHashChange);
    }, 1000);
}

function checkForInitialSection(sectionLinks: SectionLink[], navbarThreshold: number): void {
    if (window.location.hash.length <= 1) return;

    const decodedHash = decodeURI(window.location.hash).replace('#', '');
    const section = document.getElementById(decodedHash) as Section | undefined;
    if (!section) return;
    const sectionLink = sectionLinks.find((e) => e.dataset.id === decodedHash.slice(1));
    if (section && sectionLink) {
        scrollToSection(section);
        setActiveSection(sectionLink);
        toggleFixed(document.querySelector<HTMLElement>('.js-local-nav'), navbarThreshold);
    }
}

function toggleFixed(navbar: HTMLElement | null, threshold: number): void {
    if (!navbar) return;

    const windowY = window.scrollY;
    if (windowY >= threshold && !navbar.classList.contains('fixed')) {
        navbar.classList.add('fixed');
    }
    if (windowY <= threshold && navbar.classList.contains('fixed')) {
        document.querySelector('.active')?.classList.remove('active');
        navbar.classList.remove('fixed');
    }
}

function findActiveSection(sections: Section[]): Section | undefined {
    const LOCAL_NAV_HEIGHT = 78;
    const windowY = window.scrollY;
    return sections
        .slice()
        .reverse()
        .find((sec) => sec.offsetTop - LOCAL_NAV_HEIGHT < windowY);
}

function setupToggle(navbar: HTMLElement): void {
    document.querySelectorAll<HTMLElement>('.jump-to')?.forEach((el) => {
        el.addEventListener('click', (ev) => {
            ev.preventDefault();
            navbar.classList.toggle('expanded');
        });
    });
}

function setupChildNav(): void {
    const pageElementsWithBg = document.querySelectorAll<HTMLElement>('.rows [class*="block-"], .rows [class*="bg-"]');
    const wrapper = document.querySelector<HTMLElement>('.page-bg-wrapper');

    if (wrapper && pageElementsWithBg.length > 0) {
        const pageTopElementBGClass = Array.from(pageElementsWithBg[0].classList).find(
            (e) => e.startsWith('block-') || e.startsWith('bg-'),
        );

        if (pageTopElementBGClass) {
            wrapper.classList.add(pageTopElementBGClass);
        }
    }
}

function init(): void {
    const childNav = document.querySelector<HTMLElement>('.js-child-nav');
    if (childNav) {
        setupChildNav();
        return setupToggle(childNav);
    }
    const navbar = document.querySelector<HTMLElement>('.js-local-nav');
    if (!navbar) return;

    const header = document.querySelector<HTMLElement>('bi-navbar');
    const headerHeight = header ? header.offsetHeight : 80;
    const navbarThreshold = navbar.offsetTop - headerHeight;

    const sections = Array.from(document.querySelectorAll<Section>('.section'));
    const links = Array.from(document.querySelectorAll<SectionLink>('.section-link'));

    setTimeout(() => checkForInitialSection(links, navbarThreshold), 500);

    document.addEventListener(
        'scroll',
        throttle(() => {
            if (!animating) {
                toggleFixed(navbar, navbarThreshold);
            }
            if (navbar.classList.contains('fixed')) {
                const section = findActiveSection(sections);
                if (!section) return;
                const link = links.find((el) => el.dataset.id === section.id);
                if (!link) return;
                setActiveSection(link);
            }
        }, 50),
    );

    setupToggle(navbar);

    document.querySelectorAll<SectionLink>('.section-link').forEach((el) => {
        el.addEventListener('click', (ev) => {
            ev.preventDefault();
            if (!el.classList.contains('practical-info-link')) {
                setActiveSection(el);
                const activeSection = findSection(el.dataset.id, sections);
                if (activeSection) scrollToSection(activeSection);
            }
        });
    });

    document.querySelectorAll<HTMLElement>('.practical-info-link').forEach((el) => {
        el.addEventListener('click', (ev) => {
            ev.preventDefault();
            const practicalInfo = document.querySelector<Section>('.practical-info');
            if (practicalInfo) scrollToSection(practicalInfo);
        });
    });

    const home = document.getElementById('home');
    if (home) {
        home.addEventListener('click', () => {
            if (sections[0]) scrollToSection(sections[0]);
        });
    }
}

onReady(() => init());
